<template>
  <section>
    <section class="page-stack grid-stack grid-gap-page-section">
      <section
        v-if="state.isLoading"
        class="page-panel grid-stack grid-gap-12 justify-self-center text-center"
      >
        <v-progress-circular indeterminate />
        <p class="panel-description">Učitavanje dokumenta...</p>
      </section>

      <section
        v-else-if="state.loadErrorMessage"
        class="form page-panel grid-stack grid-gap-20"
      >
        <p class="notice">{{ state.loadErrorMessage }}</p>

        <v-btn
          class="button button--secondary button--small justify-self-start"
          :to="backRoute"
          variant="outlined"
        >
          {{ isDocumentPreview ? 'Natrag na popis dokumenata' : 'Natrag na profil' }}
        </v-btn>
      </section>

      <v-form
        v-else
        ref="detailsFormRef"
        class="form document-signing body-only-form"
        @submit.prevent="continueToSignatureAsync"
      >
        <section class="body-only-form__header grid-stack grid-gap-8">
          <router-link
            class="back-link"
            :to="backRoute"
            aria-label="Natrag"
          >
            <v-icon
              icon="mdi-arrow-left"
              size="20"
            />
            <span>Natrag</span>
          </router-link>

          <header>
            <h2 class="h3">
              {{ state.document.name }}
            </h2>

            <p
              v-if="state.document.intro"
              class="panel-description"
            >
              {{ state.document.intro }}
            </p>
          </header>
        </section>

        <div class="body-only-form__content document-signing__content grid-stack grid-gap-12">
          <section class="grid-stack grid-gap-12">
            <header>
              <h3 class="panel-title">
                Dodatne informacije
              </h3>
            </header>

            <div
              v-if="isDocumentPreview"
              class="grid-stack grid-gap-16"
            >
              <section class="grid-stack grid-gap-8">
                <h4 class="ma-0">Automatski popunjeni podaci</h4>
                <ul class="document-signing__field-list">
                  <li>Datum početka - datum završetka</li>
                  <li>Naziv događaja</li>
                  <li>Lokacija</li>
                  <li>Ime</li>
                </ul>
              </section>

              <section
                v-if="state.document.eventCustomFieldPlaceholder"
                class="grid-stack grid-gap-8"
              >
                <h4 class="ma-0">Podaci o događaju (unosi brand manager)</h4>
                <ul class="document-signing__field-list">
                  <li>{{ state.document.eventCustomFieldPlaceholder.replace(/\s*\*$/, '') }}</li>
                </ul>
              </section>

              <section
                v-if="state.document.inviteeCustomFieldPlaceholder"
                class="grid-stack grid-gap-8"
              >
                <h4 class="ma-0">Podaci koje unosi uzvanik</h4>
                <ul class="document-signing__field-list">
                  <li>{{ state.document.inviteeCustomFieldPlaceholder.replace(/\s*\*$/, '') }}</li>
                </ul>
              </section>
            </div>

            <div
              v-else
              class="grid grid-row-gap-12 grid-column-gap-16"
            >
              <div class="col-12 col-sm-6">
                <AppTextField
                  label="Datum početka - datum završetka"
                  :model-value="eventPeriod"
                  readonly
                />
              </div>

              <div class="col-12 col-sm-6">
                <AppTextField
                  label="Naziv događaja"
                  :model-value="state.event?.name ?? ''"
                  readonly
                />
              </div>

              <div class="col-12 col-sm-6">
                <AppTextField
                  label="Lokacija"
                  :model-value="state.event?.location ?? ''"
                  readonly
                />
              </div>

              <div class="col-12 col-sm-6">
                <AppTextField
                  label="Ime"
                  :model-value="state.invitee?.fullName ?? ''"
                  readonly
                />
              </div>

              <div
                v-if="state.document.customFieldValue && state.document.slug !== 'consent'"
                class="col-12"
              >
                <AppTextField
                  :label="state.document.eventCustomFieldPlaceholder?.replace(/\s*\*$/, '') || 'Dodatna informacija događaja'"
                  :model-value="state.document.customFieldValue"
                  readonly
                />
              </div>

              <div
                v-if="state.document.inviteeCustomFieldPlaceholder"
                class="col-12"
              >
                <AppTextField
                  v-model="state.customFieldValue"
                  :label="state.document.inviteeCustomFieldPlaceholder.replace(/\s*\*$/, '')"
                  :placeholder="isResidenceField ? 'Unesite prebivalište' : 'Unesite'"
                  :rules="[(value) => (value ?? '').length <= 255 || 'Dopušteno je najviše 255 znakova']"
                  required
                />
              </div>
            </div>
          </section>

          <section class="grid-stack grid-gap-12">
            <header>
              <h3 class="panel-title document-signing__document-title">
                Sadržaj dokumenta
              </h3>
            </header>

            <div class="document-content document-signing__document grid-stack grid-gap-8">
              <p
                v-if="state.document.previewText"
                class="document-signing__preview document-signing__preview--text"
              >
                {{ state.document.previewText }}
              </p>
              <p
                v-else
                class="notice"
              >
                Pregled dokumenta nije dostupan.
              </p>
            </div>
          </section>
        </div>

        <div
          v-if="!isDocumentPreview"
          class="body-only-form__actions"
        >
          <div class="document-signing__consent">
            <v-checkbox
              v-model="state.acceptedTerms"
              class="checkbox"
              density="compact"
              hide-details="auto"
              :label="documentConsentText"
              :rules="[(value) => value || 'Potrebno je prihvatiti uvjete']"
            />
          </div>

          <ToolbarActionButton
            class="body-only-form__button"
            :disabled="state.isSaving || !isDocumentAvailable || !state.acceptedTerms"
            icon=""
            :loading="state.isSaving"
            text="Nastavi"
            type="submit"
          />
        </div>
      </v-form>
    </section>

    <v-dialog
      v-if="!isDocumentPreview"
      :model-value="state.isSignatureDialogOpen"
      :fullscreen="isMobileSignature"
      :width="isMobileSignature ? undefined : 852"
      :height="isMobileSignature ? undefined : 393"
      :persistent="state.isSaving"
      aria-labelledby="document-signature-title"
      @update:model-value="!$event && closeSignatureDialog()"
    >
      <form
        class="document-signing__signature"
        @submit.prevent="saveSignedDocumentAsync"
      >
        <header class="document-signing__signature-header">
          <div>
            <h2
              id="document-signature-title"
              class="document-signing__signature-title"
            >
              {{ state.document.name }}
            </h2>
            <p class="text-mid document-signing__signature-intro">Dodaj potpis</p>
          </div>

          <v-btn
            class="button button--icon dialog-close"
            aria-label="Zatvori potpis"
            :disabled="state.isSaving"
            icon="mdi-close"
            size="x-small"
            variant="text"
            @click="closeSignatureDialog"
          />
        </header>

        <SignaturePad
          v-model="state.signature"
          :error-message="state.signatureErrorMessage"
          @update:model-value="state.signatureErrorMessage = ''"
        />

        <footer class="document-signing__signature-actions">
          <v-btn
            class="button button--primary button--toolbar document-signing__signature-button"
            :disabled="state.isSaving"
            :loading="state.isSaving"
            type="submit"
            variant="flat"
          >
            Potpiši dokument
          </v-btn>
        </footer>
      </form>
    </v-dialog>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import AppTextField from '../../components/AppTextField.vue'
import SignaturePad from '../../components/SignaturePad.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { formatDate } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
const isDocumentPreview = route.name === 'event-document-detail'
const { mobile: isNarrowViewport, height: viewportHeight, platform } = useDisplay({ mobileBreakpoint: 768 })
// Keep phone signatures fullscreen in landscape without treating short desktop windows as mobile.
const isMobileSignature = computed(() => isNarrowViewport.value || (
  (platform.value.android || platform.value.ios) && viewportHeight.value < 768
))
const detailsFormRef = ref(null)
let isUnmounted = false
const state = reactive({
  event: null,
  invitee: null,
  document: null,
  customFieldValue: '',
  acceptedTerms: false,
  signature: '',
  signatureErrorMessage: '',
  isSignatureDialogOpen: false,
  isLoading: true,
  isSaving: false,
  loadErrorMessage: '',
})

const backRoute = computed(() => isDocumentPreview
  ? { name: 'event-documents' }
  : {
      name: 'event-invitee-detail',
      params: {
        eventId: route.params.eventId,
        inviteeId: route.params.inviteeId,
      },
      query: route.query,
    })
const eventPeriod = computed(() => state.event
  ? `${formatDate(state.event.startDate)} - ${formatDate(state.event.endDate)}`
  : '')
const isResidenceField = computed(() => (
  state.document.inviteeCustomFieldPlaceholder?.toLocaleLowerCase('hr').includes('prebivali')
))
const documentConsentText = 'Pročitao/la sam i slažem se s uvjetima navedenim u ovom dokumentu. Označavanjem ovog polja potvrđujem da dokument potpisujem elektronički svojim punim pravnim imenom.'
const isDocumentAvailable = computed(() => Boolean(state.document?.previewText))

onMounted(loadDocumentAsync)
onBeforeUnmount(() => {
  isUnmounted = true
})

async function loadDocumentAsync () {
  state.isLoading = true
  state.loadErrorMessage = ''

  try {
    if (isDocumentPreview) {
      const documentTypes = await apiClient.getAsync('/api/document-types')

      if (isUnmounted) {
        return
      }

      state.document = documentTypes.find((documentType) => documentType.id === route.params.documentId)
      if (!state.document) {
        state.loadErrorMessage = 'Dokument nije pronađen.'
      }
      return
    }

    const event = await apiClient.getAsync(`/api/events/${route.params.eventId}`)

    if (isUnmounted) {
      return
    }

    if (event.isArchived) {
      showErrorToast('Dokumente arhiviranog događaja nije moguće potpisivati.')
      await router.replace(backRoute.value)
      return
    }

    const invitee = event.invitees.find(({ id }) => id === route.params.inviteeId)
    const eventDocument = event.documents.find(({ id }) => id === route.params.documentId)

    if (!invitee || !eventDocument) {
      throw new Error('The invitee or document is not assigned to this event.')
    }

    const inviteeDocument = invitee.documents?.find(({ id }) => id === route.params.documentId)

    if (inviteeDocument?.signedAt || inviteeDocument?.signedPdf) {
      showErrorToast('Ovaj dokument je već potpisan.')
      await router.replace(backRoute.value)
      return
    }

    state.event = event
    state.invitee = invitee
    state.document = eventDocument
    state.customFieldValue = inviteeDocument?.customFieldValue ?? (isResidenceField.value ? invitee.residence : '') ?? ''
  } catch (error) {
    console.error('Failed to load document signing data.', error)
    state.loadErrorMessage = isDocumentPreview
      ? 'Dokument nije bilo moguće učitati.'
      : 'Dokument za potpisivanje nije bilo moguće učitati.'
    if (!isUnmounted) {
      showRequestErrorToast(error, state.loadErrorMessage)
    }
  } finally {
    state.isLoading = false
  }
}

async function continueToSignatureAsync () {
  if (!isDocumentAvailable.value || !state.acceptedTerms) {
    return
  }

  const { valid } = await detailsFormRef.value.validate()

  if (valid && !isUnmounted) {
    state.isSignatureDialogOpen = true
  }
}

function closeSignatureDialog () {
  state.isSignatureDialogOpen = false
  state.signature = ''
  state.signatureErrorMessage = ''
}

async function saveSignedDocumentAsync () {
  if (state.isSaving || !isDocumentAvailable.value || !state.acceptedTerms) {
    return
  }

  if (!state.signature) {
    state.signatureErrorMessage = 'Potpis je obavezan'
    return
  }

  state.isSaving = true
  state.signatureErrorMessage = ''

  try {
    await apiClient.postAsync(`/api/events/${state.event.id}/invitee-documents`, {
      documentTypeId: state.document.id,
      eventInviteeId: state.invitee.id,
      customFieldValue: state.document.inviteeCustomFieldPlaceholder
        ? state.customFieldValue.trim()
        : null,
      signature: state.signature,
    })

    if (isUnmounted) {
      return
    }

    showSuccessToast('Dokument je uspješno potpisan.')
    await router.push(backRoute.value)
  } catch (error) {
    console.error('Failed to sign invitee document.', error)
    if (!isUnmounted) {
      showRequestErrorToast(error, 'Dokument nije bilo moguće potpisati.')
    }
  } finally {
    state.isSaving = false
  }
}
</script>

<style scoped lang="scss">
.document-signing {
  &__field-list {
    margin: 0;
    padding-inline-start: 20px;
    color: var(--color-black);
    font-size: 15px;
    font-weight: 500;
    line-height: 22px;

    li + li {
      margin-top: 8px;
    }
  }

  &__content {
    :deep(.v-field:has(input[readonly])) {
      background: var(--color-surface-hover);
    }
  }

  &__document-title,
  &__consent :deep(.checkbox:not(.v-input--error) .v-label) {
    color: #101828;
  }

  &__document-title {
    line-height: clamp-bp("sm", "xl", 20, 26);
    letter-spacing: -0.15px;
  }

  &__document {
    border: 1px solid var(--body-only-form-border);
  }

  &__preview {
    max-width: 100%;

    &--text {
      margin: 0;
      padding: 16px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  &__consent {
    padding: 13px;
    border: 1px solid #dbeafe;
    border-radius: 10px;
    background: var(--color-info-background);

    :deep(.checkbox .v-label) {
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.15px;
    }

    :deep(.v-selection-control) {
      align-items: flex-start;
      gap: 8px;
    }

    :deep(.checkbox:not(.v-input--error) .mdi-checkbox-blank-outline) {
      width: 20px;
      height: 20px;
      border: 1px solid rgba(26, 26, 26, 0.2);
      border-radius: 3px;
      background: var(--color-white);
      font-size: 0;
      opacity: 1;
    }
  }

  &__signature {
    height: 100%;
    background: var(--color-white);
  }

  &__signature-header,
  &__signature-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__signature-header {
    justify-content: space-between;
    gap: 16px;
    height: 68px;
    padding: 0 24px;
    border-bottom: 1px solid var(--color-border);
  }

  &__signature-title {
    font-size: 18px;
    line-height: 28px;
    letter-spacing: -0.44px;
  }

  &__signature-intro {
    margin: 0;
    color: var(--color-text-muted);
    font-size: 14px;
    line-height: 20px;
  }

  &__signature-actions {
    justify-content: flex-end;
    height: 62px;
    padding: 0 16px;
    border-top: 1px solid var(--color-border);
  }

  &__signature-button.v-btn {
    width: 174px;
    height: 42px;
  }
}
</style>
